import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import isEmpty from 'lodash/isEmpty';
import { Tooltip } from 'antd';
import { es } from 'date-fns/locale';
import { IQuote } from '@share/common-types';
import { ILoginState, IMarginatorState } from '@share/store/slices';
import { RefundableEnum } from '@common-types';
import { formatDateCheckInOut, isSpanish, renderNumber } from '@share/utils';
import { Currency } from '@components';
import { DATE_FORMAT_CHECK_IN_OUT } from '@constants';
import { getModalCompareMealPlanBadgeKind, MODAL_COMPARE_MEAL_PLAN_BADGE_COLORS } from '../../../result/result-wrapper/modal-compare/meal-plan-badge-theme';
import { ExpiredQuoteNotice } from '../expired-notice';
import { buildSearchAgainUrl } from '../search-again-url';

interface IActivityTabProps {
  activities: IQuote[];
  marginatorStore: IMarginatorState;
  loginStore: ILoginState;
  onCancel: () => void;
  isHistorical?: boolean;
}

const percents = 100;

export const ActivityTab: React.FC<IActivityTabProps> = ({
  activities,
  isHistorical,
}) => {
  const firstActivity = activities[0];

  // "Search again" link for the expired notice: the first activity's link.
  const searchAgainUrl = useMemo(
    () => buildSearchAgainUrl((activities[0] as any)?.link, 'activity'),
    [activities]
  );
  const compareRowHasMealPlan = activities.some((q) => !isEmpty(q.mealPLan?.trim()));

  const handleView = (quote: IQuote) => {
    const link = quote.link.startsWith('http') ? quote.link : `${window.location.origin}${quote.link}`;
    const [basePart, queryPart] = link.split('?');
    const queryWithoutQuote = (queryPart || '')
      .replace(/&?quote=[^&]*/g, '')
      .replace(/^&/, '');
    const linkWithoutQuote = queryWithoutQuote ? `${basePart}?${queryWithoutQuote}` : basePart;
    const sep = linkWithoutQuote.includes('?') ? '&' : '?';
    window.open(`${linkWithoutQuote}${sep}quote=${quote.userWhoShared}`, '_blank');
  };

  return (
    <div>
      <div className="modal-compare__hotels modal-compare__hotels--meta">
        {firstActivity?.checkIn ? (
          <div className="hotels-dates">
            {formatDateCheckInOut(firstActivity.checkIn, DATE_FORMAT_CHECK_IN_OUT, isSpanish() ? es : undefined)}
          </div>
        ) : null}
        {firstActivity?.occupancy ? <div className="hotels-dates">{firstActivity.occupancy}</div> : null}
      </div>

      {isHistorical ? <ExpiredQuoteNotice searchAgainUrl={searchAgainUrl} /> : null}

      <div className="modal-compare__hotels">
        {activities.map((quote, index) => {
          const savings =
            quote.publicPrice && quote.price && quote.publicPrice > 0
              ? Math.floor(percents - (quote.price * percents) / quote.publicPrice)
              : 0;

          return (
            <div key={index} className="modal-compare__hotel">
              <div className="modal-compare__media">
                <div className="hotel-images-container">
                  <div className="hotel-image" style={{ backgroundImage: `url(${quote.image})` }} />
                  {savings > 0 ? (
                    <div className="modal-compare__savings-badge">
                      {savings.toFixed(0)} % <FormattedMessage id="results.hotel.card.compare.modal.off" />
                    </div>
                  ) : null}
                </div>
              </div>

              <div
                className={`hotel-text-container hotel-text-container--title${compareRowHasMealPlan ? ' hotel-text-container--title-with-mealplan' : ''
                  }`}
              >
                {compareRowHasMealPlan ? (
                  <div className="modal-compare__title-mealplan-row">
                    <div className="modal-compare__title-col">
                      <Tooltip title={quote.displayName} placement="top">
                        <p>{quote.displayName}</p>
                      </Tooltip>
                    </div>
                    <div className="modal-compare__meal-plan-col">
                      {!isEmpty(quote.mealPLan?.trim()) ? (
                        <span
                          className="modal-compare__meal-plan-badge"
                          style={
                            MODAL_COMPARE_MEAL_PLAN_BADGE_COLORS[
                            getModalCompareMealPlanBadgeKind(quote.mealPLan)
                            ]
                          }
                        >
                          {quote.mealPLan}
                        </span>
                      ) : null}
                    </div>
                  </div>
                ) : (
                  <Tooltip title={quote.displayName} placement="top">
                    <p>{quote.displayName}</p>
                  </Tooltip>
                )}
              </div>

              <div className="hotel-text-container hotel-text-container--address">
                <Tooltip title={quote.fullAddress} placement="top">
                  <p>{quote.fullAddress}</p>
                </Tooltip>
              </div>

              {quote.refundability && quote.refundability !== RefundableEnum.Unknown ? (
                <div className="hotel-text-container hotel-text-container--refundable">
                  <div className="hotel-card-wrapper__refundable">
                    {quote.refundability === RefundableEnum.Refundable && (
                      <div className="hotel-card-wrapper__refundable-ref">
                        <FormattedMessage id="refundable" />
                      </div>
                    )}
                    {quote.refundability === RefundableEnum.Nonrefundable && (
                      <div className="hotel-card-wrapper__refundable-nonref">
                        <FormattedMessage id="non.refundable" />
                      </div>
                    )}
                  </div>
                </div>
              ) : null}

              <div
                className={`hotel-text-container hotel-text-container--price${isHistorical ? ' hotel-text-container--flush-bottom' : ''
                  }`}
              >
                {quote.publicPrice ? (
                  <p style={{ textDecoration: 'line-through' }}>
                    <Currency currency={quote.currency} />
                    {renderNumber(quote.publicPrice, 2)}
                  </p>
                ) : null}
                <p>
                  <strong>
                    <Currency currency={quote.currency} />
                    {renderNumber(quote.price, 2)}
                  </strong>
                </p>
              </div>

              {!isHistorical ? (
                <div className="hotel-view-container">
                  <div
                    className="view-button"
                    style={(quote as any).isPast ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
                    onClick={() => { if (!(quote as any).isPast) handleView(quote); }}
                  >
                    <FormattedMessage id="results.hotel.card.compare.modal.view" />
                  </div>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
};
